import React, { useCallback, useEffect, useState } from "react";
import AdminHeader from "./Header";
import { formatMonthDateYear, formatShortMonthDateYear } from "../../include/function";
import { adminReqConfig, api } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import { useParams } from "react-router-dom";
import "../../styles/Super.css";

const CouponDetails = () => {
  const toast = useNotification();
  const { id } = useParams();

  const [couponDetails, setCouponDetails] = useState({});
  const [fetchLoading, setFetchLoading] = useState(false);

  const getCouponDetails = useCallback(() => {
    setFetchLoading(true);
    api
      .get(`/voucher/?id=${id}`, adminReqConfig())
      .then((response) => {
        if (response?.status === 200) {
          const coupon = Array.isArray(response.data.data)
            ? response.data.data[0]
            : response.data.data;
          setCouponDetails(coupon || {});
        }
      })
      .catch(() => {
        toast({ type: "error", message: "Failed to load coupon details." });
      })
      .finally(() => setFetchLoading(false));
  }, [id, toast]);

  useEffect(() => {
    getCouponDetails();
  }, []);

  const formatDateRange = (start, end) => {
    if (!start || !end) return "N/A";
    return `${formatShortMonthDateYear(start)} - ${formatShortMonthDateYear(
      end
    )}`;
  };

  const joinArray = (arr, separator = ", ") => {
    if (!Array.isArray(arr)) return "N/A";
    return arr.length > 0 ? arr.join(separator) : "N/A";
  };

  const formatCurrency = (value) => {
    if (isNaN(value)) return "₹ 0.00";
    return `₹ ${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  if (fetchLoading) {
    return (
      <AdminHeader title={"COUPON DETAILS"}>
        <div className="content-wrapper p-0">
          <section
            style={{
              backgroundColor: "#F1F4F9",
              padding: "30px",
              marginTop: "70px",
              marginBottom: "40px",
              minHeight: "80vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            Loading...
          </section>
        </div>
      </AdminHeader>
    );
  }

  return (
    <>
      <AdminHeader title={"COUPON DETAILS"}>
        <div className="content-wrapper p-0">
          <section
            style={{
              backgroundColor: "#F1F4F9",
              padding: "30px",
              marginTop: "70px",
              marginBottom: "40px",
              minHeight: "80vh",
            }}
          >
            <div className="row g-3 mb-3">
              {/* LEFT COLUMN - COUPON INFO */}
              <div className="col-8">
                <div
                  className="order-detail-card shadow-sm"
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "20px",
                  }}
                >
                  <div
                    className="mb-3"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "16px",
                    }}
                  >
                    <h6
                      style={{
                        fontWeight: "700",
                        fontSize: "18px",
                        color: "#2c3e50",
                        margin: 0,
                      }}
                    >
                      Coupon Code{" "}
                      <span style={{ color: "#9da6b5" }}>
                        #{couponDetails?.code}
                      </span>
                      <span
                        style={{
                          marginLeft: "10px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600",
                          backgroundColor:
                            couponDetails?.appliedCount > 0
                              ? "#d4edda"
                              : "#f8d7da",
                          color:
                            couponDetails?.appliedCount > 0
                              ? "#155724"
                              : "#721c24",
                        }}
                      >
                        {couponDetails?.appliedCount > 0
                          ? "Applied"
                          : "Not Used"}
                      </span>
                    </h6>
                  </div>

                  <div className="row g-4 mt-14">
                    <div className="col-md-4">
                      <div style={{ fontSize: "14px", color: "black" }}>
                        <strong>Customer Name</strong>
                        <p className="payment-details-p">
                          {joinArray(couponDetails?.customerName)}
                        </p>{" "}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{ fontSize: "14px", color: "black" }}>
                        <strong>Customer Type</strong>
                        {couponDetails?.customerType?.map((type) => {
                          // Determine group from type
                          const groupLabel =
                            type === "individual" ? "B2C" : "B2B";
                          return (
                            <p
                              key={type}
                              className="payment-details-p"
                              style={{ margin: "4px 0" }}
                            >
                              {type
                                .replace(/-/g, " ")
                                .replace(/\b\w/g, (char) =>
                                  char.toUpperCase()
                                )}{" "}
                              ({groupLabel})
                            </p>
                          );
                        }) || <p className="payment-details-p">N/A</p>}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{ fontSize: "14px", color: "black" }}>
                        <strong>Product</strong>
                        <p className="payment-details-p">
                          {joinArray(couponDetails?.productName)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="row g-4 mt-14">
                    <div className="col-md-4">
                      <div style={{ fontSize: "14px", color: "black" }}>
                        <strong>Category</strong>
                        <p className="payment-details-p">
                          {joinArray(couponDetails?.categoryName)}
                        </p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div
                        style={{
                          fontSize: "14px",
                          color: "black",
                        }}
                      >
                        <strong>Brand</strong>
                        <p className="payment-details-p">
                          {joinArray(couponDetails?.brandName)}
                        </p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div
                        style={{
                          fontSize: "14px",
                          color: "black",
                        }}
                      >
                        <strong>Minimum Requirements</strong>
                        <p className="payment-details-p">
                          {couponDetails?.basedOn === "quantity"
                            ? `${couponDetails?.minRequirement} ${
                                couponDetails?.minRequirement > 1
                                  ? "items"
                                  : "item"
                              }`
                            : formatCurrency(couponDetails?.minRequirement)}
                        </p>
                      </div>
                    </div>

                    <div className="col-4">
                      <div style={{ fontSize: "14px", color: "black" }}>
                        <strong>Coupon Type</strong>
                        <p className="payment-details-p">
                          {couponDetails?.type}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN - COUPON STATS */}
              <div className="col-4">
                <div className="payment-detail-card shadow-sm mb-3">
                  <h6 className="payment-details-right-col-h6">Coupon Value</h6>
                  <p className="payment-details-right-col-p">
                    {couponDetails?.type === "percentage"
                      ? `${couponDetails?.value}%`
                      : formatCurrency(couponDetails?.value)}
                  </p>
                </div>

                {/* COUPON VALIDITY */}
                <div className="payment-detail-card shadow-sm mb-3">
                  <h6 className="payment-details-right-col-h6">
                    Coupon Validity
                  </h6>
                  <p className="payment-details-right-col-p">
                    {formatDateRange(
                      couponDetails?.startDate,
                      couponDetails?.endDate
                    )}
                  </p>
                </div>

                {/* USAGE LIMIT */}
                <div className="payment-detail-card shadow-sm mb-3">
                  <h6 className="payment-details-right-col-h6">Usage Limit</h6>

                  <div style={{ marginTop: "15px" }}>
                    <div className="mt-24">
                      <h6 className="payment-details-right-col-h6">Total</h6>
                      <p className="payment-details-right-col-p">
                        {couponDetails?.maxCount}
                      </p>
                    </div>
                    <div className="mt-24">
                      <h6 className="payment-details-right-col-h6">Used</h6>
                      <p className="payment-details-right-col-p">
                        {couponDetails?.appliedCount}
                      </p>
                    </div>
                    <div className="mt-24">
                      <h6 className="payment-details-right-col-h6">Balance</h6>
                      <p className="payment-details-right-col-p">
                        {Math.max(
                          0,
                          (couponDetails?.maxCount || 0) -
                            (couponDetails?.appliedCount || 0)
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </AdminHeader>
    </>
  );
};

export default CouponDetails;
